import React, { useRef, useState } from "react";
import { formatDueDate, isOverdue } from "../actionItemHelpers";
import ActionItemDueDatePicker from "./ActionItemDueDatePicker";

const CalendarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="1" y="2.5" width="12" height="10.5" rx="1.5" />
    <path d="M4 1v3M10 1v3M1 6h12" strokeLinecap="round" />
  </svg>
);

/**
 * Calendar icon + due-date badge/picker (same UX as action item rows).
 *
 * @example
 * <ActionItemDueDateControl value="2026-05-26" onChange={setDueDate} />
 * // With a date: clickable "Mon, 6th June" badge opens the picker.
 * // Without a date: calendar icon only (or icon + "Set due date" when showEmptyHint).
 */
const ActionItemDueDateControl = ({
  value = "",
  onChange,
  disabled = false,
  className = "",
  showEmptyHint = false,
}) => {
  const wrapRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const dueDateIso = String(value || "").trim().slice(0, 10);
  const isAddFormDueWrap = String(className).includes("action-items-add-form__due-wrap");
  const pickerPlacement = isAddFormDueWrap ? "above" : "auto";
  const pickerAlign = isAddFormDueWrap ? "end" : "start";
  const dueDateLabel = formatDueDate(dueDateIso);
  const itemIsOverdue = isOverdue({ due_date: dueDateIso });

  const togglePicker = (event) => {
    event?.stopPropagation?.();
    if (disabled) return;
    setPickerOpen((open) => !open);
  };

  const wrapClass = ["action-item-due-btn-wrap", className].filter(Boolean).join(" ");

  return (
    <div ref={wrapRef} className={wrapClass}>
      {dueDateLabel ? (
        <button
          type="button"
          className={`action-item-due-badge${itemIsOverdue ? " action-item-due-badge--overdue" : ""}`}
          title="Edit due date"
          aria-label="Edit due date"
          disabled={disabled}
          onClick={togglePicker}
        >
          {dueDateLabel}
        </button>
      ) : showEmptyHint ? (
        <button
          type="button"
          className={`action-item-due-empty-hint${
            itemIsOverdue ? " action-item-due-empty-hint--overdue" : ""
          }`}
          title="Set due date"
          aria-label="Set due date"
          disabled={disabled}
          onClick={togglePicker}
        >
          <CalendarIcon />
          <span>Set due date</span>
        </button>
      ) : (
        <button
          type="button"
          className={`action-item-due-icon-btn action-item-due-icon-btn--compact${
            itemIsOverdue ? " action-item-due-icon-btn--overdue" : ""
          }`}
          data-tip="Set due date"
          aria-label="Set due date"
          disabled={disabled}
          onClick={togglePicker}
        >
          <CalendarIcon />
        </button>
      )}
      {pickerOpen && !disabled ? (
        <ActionItemDueDatePicker
          anchorRef={wrapRef}
          placement={pickerPlacement}
          align={pickerAlign}
          value={dueDateIso}
          onSelect={(iso) => {
            onChange(iso);
            setPickerOpen(false);
          }}
          onClear={() => {
            onChange("");
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default ActionItemDueDateControl;
